import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Pause, Play } from 'lucide-react';
import { useLang } from '../../i18n';
import { Reveal } from '../Reveal';

/** Carrellata reale nello showroom: 6,5s, 716 KB, niente audio, niente sottotitoli bruciati nel video. */
export const ShowroomReel = () => {
  const { t } = useLang();
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [reduced, setReduced] = useState(false);
  const [visibile, setVisibile] = useState(false);
  // true finche' il video non parte davvero (onPlay): sotto "riduci animazioni"
  // non parte mai da solo, e il pulsante deve dire "Riproduci".
  const [inPausa, setInPausa] = useState(true);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  /**
   * Il file pesa 700 KB e la sezione sta a ~7800px dall'inizio della pagina:
   * senza questo osservatore il video si scaricava sempre, anche per chi non
   * arrivava mai a vederlo. `preload="none"` da solo non bastava, perche'
   * `autoPlay` lo annulla.
   */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibile(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const riproduci = visibile && !reduced;

  const alternaPausa = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  return (
    <section
      ref={sectionRef}
      data-testid="home-showroom"
      className="relative overflow-hidden bg-inchiostro text-carta"
    >
      <div className="relative aspect-[4/5] w-full sm:aspect-[16/9] lg:aspect-[21/9]">
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          poster="/assets/video/showroom-poster.jpg"
          width="960"
          height="540"
          autoPlay={riproduci}
          loop
          muted
          playsInline
          preload="none"
          onPlay={() => setInPausa(false)}
          onPause={() => setInPausa(true)}
        >
          {visibile && <source src="/assets/video/showroom.mp4" type="video/mp4" />}
        </video>
        {visibile && (
          <button
            type="button"
            onClick={alternaPausa}
            data-testid="home-showroom-toggle"
            aria-label={inPausa ? t('common.playVideo') : t('common.pauseVideo')}
            className="absolute right-4 top-4 z-10 grid h-11 w-11 place-items-center rounded-full bg-inchiostro/70 text-carta transition-colors hover:bg-inchiostro"
          >
            {inPausa ? (
              <Play size={16} strokeWidth={2} className="ml-0.5" />
            ) : (
              <Pause size={16} strokeWidth={2} />
            )}
          </button>
        )}
        {/* velatura piatta: il titolo si legge sul video senza sfumature */}
        <div className="absolute inset-0 bg-inchiostro/40" />
        <div className="wrap absolute inset-x-0 bottom-0 pb-10 md:pb-14">
          <Reveal>
            <h2 className="max-w-[20ch] text-[36px] leading-[1.04] md:text-[48px]">{t('home.showroomTitle')}</h2>
            <p className="mt-3 text-[18px] text-carta/90">{t('home.showroomKicker')}</p>
            <Link
              to="/azienda"
              data-testid="home-showroom-cta"
              className="btn btn-line-light mt-7 inline-flex"
            >
              {t('home.showroomCta')}
              <ArrowUpRight size={15} strokeWidth={2} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
